import React, { useCallback, useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useSelector } from 'react-redux';

import { useStopImpersonationMutation, useTransferSubscriptionMutation } from 'api/main';
import { userSelectors } from 'reducers';
import { saveInStorage } from 'utils/helpers';
import { useSmallScreen } from 'utils/hooks';
import { texts } from 'utils/languages/fr';

import { ActionButton, CancelButton, CustomDialogTitle, CustomTextField } from 'components';
import { Typography, DialogContent, DialogActions, Dialog, InputAdornment } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

const useStyles = makeStyles(theme => ({
    paper: {
        [theme.breakpoints.down('sm')]: {
            minWidth: 'unset',
        },
    },
    dialogContent: {
        padding: theme.spacing(2, 3),
        color: theme.palette.grey[600],
    },
    input: {
        width: '50%',
    },
}));

const SubscriptionTransferDialog = ({
    open,
    handleClose,
    maxAdmissionFee = 0,
    subscriptionId,
    fromTunnel,
}) => {
    const classes = useStyles();
    const fullScreen = useSmallScreen();

    const internalUser = useSelector(userSelectors.getInternalUser);
    const [admissionFee, setAdmissionFee] = useState('');
    const [admissionFeeErrors, setAdmissionFeeErrors] = useState('');

    const [stopImpersonation, { isLoading: impersonateLoader }] = useStopImpersonationMutation();
    const [transferSubscription, { isSuccess, isError, isLoading: loading, error, reset }] =
        useTransferSubscriptionMutation();

    const handleChange = useCallback(({ target: { value } }) => {
        setAdmissionFee(value);
        setAdmissionFeeErrors('');
    }, []);

    const isAdmissionFeeValid = useCallback(() => {
        if (isNaN(parseFloat(admissionFee))) {
            setAdmissionFeeErrors(texts.VEUILLEZ_SAISIR_LE_MONTANT);
            return false;
        } else if (admissionFee > maxAdmissionFee) {
            setAdmissionFeeErrors(texts.WRONG_ADMISSION_FEE);
            return false;
        }
        return true;
    }, [admissionFee, maxAdmissionFee]);

    const handleConfirmTransfer = useCallback(() => {
        if (isAdmissionFeeValid()) {
            transferSubscription({ id: subscriptionId, admissionFee });
        }
    }, [isAdmissionFeeValid, admissionFee, transferSubscription, subscriptionId]);

    const handleCloseTransfer = useCallback(() => {
        setAdmissionFeeErrors('');
        setAdmissionFee('');
        reset();
        handleClose();
        if (fromTunnel) {
            if (internalUser?.impersonatedByUser) {
                saveInStorage('route', '/subscriptions');
                stopImpersonation();
            }
        }
    }, [handleClose, reset, fromTunnel, stopImpersonation, internalUser]);

    useEffect(() => {
        if (isError || isSuccess) {
            handleCloseTransfer();
        }
    }, [isError, isSuccess, handleCloseTransfer, error]);

    return (
        <Dialog fullScreen={fullScreen} open={open} classes={{ paper: classes.paper }}>
            <CustomDialogTitle
                title={texts.TRANSFER_TO_SUBSCRIBER}
                handleClose={handleCloseTransfer}
            />
            <DialogContent className={classes.dialogContent}>
                <CustomTextField
                    formatNumber
                    name="entryRights"
                    inputStyle={classes.input}
                    label={texts.ADMISSION_FEE}
                    value={admissionFee}
                    placeholder={`Max ${maxAdmissionFee}%`}
                    onChange={handleChange}
                    error={admissionFeeErrors}
                    endAdornment={<InputAdornment position="start">%</InputAdornment>}
                />
                <Typography gutterBottom>{texts.TRANSFER_TO_SUBSCRIBER_INFO}</Typography>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={loading || impersonateLoader} onClick={handleCloseTransfer}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton
                    loading={loading || impersonateLoader}
                    disabled={loading || impersonateLoader}
                    onClick={handleConfirmTransfer}
                >
                    {texts.ENVOYER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

SubscriptionTransferDialog.propTypes = {
    fromTunnel: PropTypes.bool,
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    subscriptionId: PropTypes.number,
    maxAdmissionFee: PropTypes.number,
};

export default SubscriptionTransferDialog;
